import {inject, injectable} from 'inversify';

import type {GoalApiModel} from '../../../../api/goal';
import type {DateTimeObj} from '../../../../utils/datetime';
import type {Nullable} from '../../../../utils/utility-types';
import type {IApiDataMapper} from '../../../ports/types';
import type {DateTimeModel} from '../../Date/DateTime.model';

import {CourseMapperJrpc} from '../../Course/mappers/Course.mapper.jrpc';
import {DateTimeModelFactory} from '../../Date/DateTime.factory';
import {ProgramMapperJrpc} from '../../Program/mappers/Program.mapper.jrpc';
import {SubgoalMapperJrpc} from '../../Subgoal/mappers/Subgoal.mapper.jrpc';
import {GoalFactory} from '../Goal.factory';
import {GoalModel} from '../Goal.model';
import {GoalExpandJrpc} from './Goal.expand.jrpc';

@injectable()
export class GoalMapperJrpc
  implements IApiDataMapper<GoalMapperJrpc.ApiData, GoalModel.Props, GoalModel>
{
  constructor(
    @inject(GoalFactory)
    private readonly factory: GoalFactory,
    @inject(GoalExpandJrpc)
    public readonly expand: GoalExpandJrpc,
    @inject(DateTimeModelFactory)
    private readonly dateTimeFactory: DateTimeModelFactory,
    @inject(SubgoalMapperJrpc)
    private readonly subgoalMapper: SubgoalMapperJrpc,
    @inject(CourseMapperJrpc)
    private readonly courseMapper: CourseMapperJrpc,
    @inject(ProgramMapperJrpc)
    private readonly programMapper: ProgramMapperJrpc,
  ) {}

  mapDataToModel(apiData: GoalMapperJrpc.ExpandedApiData): GoalModel {
    return this.factory.create(this.mapDataToProps(apiData));
  }

  mapDataToProps(data: GoalMapperJrpc.ExpandedApiData): GoalModel.Props {
    return {
      apiData: data,
      clientAttitude: data.client_attitude,
      clientComment: data.client_comment,
      clientId: data.client_id,
      coachAttitude: data.coach_attitude,
      coachComment: data.coach_comment,
      coachIds: data.coach_ids,
      color: data.color,
      counters: {
        childGoals: data.counters.child_goals,
        clientsNeedReview: data.counters.clients_need_review,
        clientsReached: data.counters.clients_reached,
        clientSubgoals: data.counters.client_subgoals,
      },
      course: this.course(data),
      courseId: data.course_id,
      created: this.dateTimeFactory.createFromDateTimeObject(data.created),
      description: data.description,
      icon: data.icon,
      id: data._id,
      isClientManaged: data.is_client_managed,
      lastActivity: this.mapOptionalDate(data.last_activity),
      notified: {
        expiringReview: this.mapOptionalDate(data.notified.expiring_review),
        reviewReminder: this.mapOptionalDate(data.notified.review_reminder),
      },
      parentGoalId: data.parent_goal_id,
      program: this.program(data),
      programId: data.program_id,
      progress: data.progress,
      reached: this.mapOptionalDate(data.reached),
      reviewUntil: this.mapOptionalDate(data.review_until),
      sortOrder: data.sort_order,
      status: data.status,
      subgoalIds: data.subgoal_ids,
      subgoals: this.mapSubgoals(data),
      title: data.title,
      updated: this.dateTimeFactory.createFromDateTimeObject(data.updated),
      userId: data.user_id,
    };
  }

  private course(data: GoalMapperJrpc.ExpandedApiData) {
    return data.course ? this.courseMapper.mapDataToModel(data.course) : null;
  }

  private mapOptionalDate(
    dateTimeObj: Nullable<DateTimeObj>,
  ): DateTimeModel | null {
    return dateTimeObj
      ? this.dateTimeFactory.createFromDateTimeObject(dateTimeObj)
      : null;
  }

  private mapSubgoals(data: GoalMapperJrpc.ExpandedApiData) {
    return data.subgoals
      .filter(Boolean)
      .map(subgoal => this.subgoalMapper.mapDataToModel(subgoal));
  }

  private program(data: GoalMapperJrpc.ExpandedApiData) {
    return data.program
      ? this.programMapper.mapDataToModel(data.program)
      : null;
  }
}

export namespace GoalMapperJrpc {
  export type ApiData = GoalApiModel;

  export type ExpandedApiData = ApiData & GoalExpandJrpc.ExpandedFields;
}
